/**
 * MuJoCo WASM Viewer
 * 
 * Loads and displays MuJoCo physics simulations in the browser.
 * Uses mujoco-js npm package for MuJoCo WebAssembly.
 * Uses Three.js for 3D rendering.
 */

import loadMujoco from 'mujoco-js'
import * as THREE from 'three'

// MuJoCo scenarios
const SCENARIOS = {
  pendulum: {
    name: 'Simple Pendulum',
    xml: `<?xml version="1.0"?>
<mujoco>
  <option timestep="0.002" gravity="0 0 -9.81"/>
  
  <default>
    <joint damping="0.01" armature="0.0001"/>
    <geom solref="0.02 1" solimp="0.9 0.95 0.001"/>
  </default>

  <worldbody>
    <light pos="0 0 2" dir="0 0 -1" diffuse="0.7 0.7 0.7"/>
    <geom name="ground" type="plane" size="2 2 0.1" rgba="0.92 0.92 0.92 1" contype="0" conaffinity="0"/>

    <!-- Pivot point at top -->
    <body name="pivot" pos="0 0 1.0">
      <geom type="sphere" size="0.02" rgba="0.3 0.3 0.3 1" contype="0" conaffinity="0"/>
      
      <!-- Pendulum body - joint is at the pivot point (0,0,0 relative to pivot body) -->
      <!-- The body itself is at the pivot, and geoms extend downward -->
      <body name="pendulum" pos="0 0 0">
        <joint name="hinge" type="hinge" axis="0 1 0" damping="0.01"/>
        <!-- Rod: cylinder from joint (0,0,0) down to -0.6 -->
        <!-- Cylinder half-height is 0.3, so center it at -0.3 to span from 0 to -0.6 -->
        <!-- Rotate 90° around X to make it vertical (MuJoCo cylinders are along Z by default) -->
        <geom name="rod" type="cylinder" size="0.01 0.3" pos="0 0 -0.3" euler="1.5708 0 0" rgba="0.3 0.3 0.3 1"/>
        <!-- Ball at end of rod (0.6 units down from pivot) -->
        <geom name="ball" type="sphere" pos="0 0 -0.6" size="0.05" rgba="0.8 0.2 0.2 1" mass="0.5"/>
      </body>
    </body>
  </worldbody>

  <!-- Initial condition: give the pendulum an initial angle so it starts swinging -->
  <keyframe>
    <key name="start" qpos="0.5"/>
  </keyframe>

</mujoco>`
  },
  double_pendulum: {
    name: 'Double Pendulum',
    xml: `<?xml version="1.0"?>
<mujoco>
  <worldbody>
    <light pos="0 0 3"/>
    <geom type="plane" size="1 1 0.1" rgba="0.8 0.8 0.8 1"/>
    <body pos="0 0 1">
      <joint type="hinge" axis="0 1 0"/>
      <geom type="box" size="0.02 0.02 0.4" rgba="0.2 0.6 0.8 1"/>
      <body pos="0 0 -0.4">
        <geom type="sphere" size="0.08" rgba="0.8 0.2 0.2 1" mass="1"/>
        <joint type="hinge" axis="0 1 0"/>
        <geom type="box" size="0.02 0.02 0.3" rgba="0.2 0.6 0.8 1"/>
        <body pos="0 0 -0.3">
          <geom type="sphere" size="0.08" rgba="0.2 0.8 0.2 1" mass="1"/>
        </body>
      </body>
    </body>
  </worldbody>
</mujoco>`
  },
  projectile: {
    name: 'Projectile Motion',
    xml: `<?xml version="1.0"?>
<mujoco>
  <worldbody>
    <light pos="0 0 3"/>
    <geom type="plane" size="2 2 0.1" rgba="0.8 0.8 0.8 1"/>
    <body pos="0 0 0.5">
      <freejoint/>
      <geom type="sphere" size="0.1" rgba="0.8 0.2 0.2 1" mass="1"/>
    </body>
  </worldbody>
</mujoco>`
  },
  spring: {
    name: 'Spring-Mass System',
    xml: `<?xml version="1.0"?>
<mujoco>
  <worldbody>
    <light pos="0 0 3"/>
    <geom type="plane" size="1 1 0.1" rgba="0.8 0.8 0.8 1"/>
    <body pos="0 0 1">
      <geom type="box" size="0.05 0.05 0.5" rgba="0.6 0.6 0.6 1"/>
      <body pos="0 0 -0.5">
        <joint type="slide" axis="0 0 1" springref="0.5"/>
        <geom type="sphere" size="0.1" rgba="0.8 0.2 0.2 1" mass="1"/>
      </body>
    </body>
  </worldbody>
</mujoco>`
  },
  bouncing_ball: {
    name: "Bouncing Ball",
    xml: `<?xml version="1.0"?>
<mujoco>
  <option timestep="0.002" gravity="0 0 -9.81" integrator="RK4"/>
  
  <default>
    <geom solref="0.02 1" solimp="0.95 0.99 0.001" friction="0.3 0.1 0.001"/>
  </default>

  <worldbody>
    <light pos="0 0 2" dir="0 0 -1" diffuse="0.7 0.7 0.7"/>
    <geom name="ground" type="plane" size="2 2 0.1" rgba="0.92 0.92 0.92 1" 
          solref="0.02 1" solimp="0.95 0.99 0.001"/>

    <!-- Bouncing ball - dropped from height with high restitution -->
    <!-- solimp format: (dmin, dmax, width, mid, power) -->
    <!-- Higher dmax (0.99) = more bouncy, lower friction = less energy loss -->
    <body name="ball" pos="0 0 1.5">
      <freejoint/>
      <geom type="sphere" size="0.1" rgba="0.8 0.2 0.2 1" mass="1.0" 
            solref="0.02 1" solimp="0.95 0.99 0.001" 
            friction="0.2 0.05 0.001"/>
    </body>

  </worldbody>

</mujoco>`
  },
  calibration: {
    name: "Calibration Test",
    xml: `<?xml version="1.0"?>
<mujoco>
  <option timestep="0.002" gravity="0 0 -9.81"/>

  <worldbody>
    <light pos="0 0 1.2" dir="0 0 -1" diffuse="0.7 0.7 0.7"/>
    <geom name="ground" type="plane" size="2 2 0.1" rgba="0.92 0.92 0.92 1" contype="0" conaffinity="0"/>

    <!-- Test objects at known positions and rotations -->
    <!-- In MuJoCo Z-up: X=right, Y=forward, Z=up -->
    <!-- Object 1: Box at origin, no rotation -->
    <body name="box1" pos="0 0 0.3">
      <geom type="box" size="0.1 0.1 0.1" rgba="1 0 0 1"/>
    </body>

    <!-- Object 2: Sphere to the right (positive X) -->
    <body name="sphere1" pos="0.3 0 0.3">
      <geom type="sphere" size="0.1" rgba="0 1 0 1"/>
    </body>

    <!-- Object 3: Sphere forward (positive Y) -->
    <body name="sphere2" pos="0 0.3 0.3">
      <geom type="sphere" size="0.1" rgba="0 0 1 1"/>
    </body>

    <!-- Object 4: Cylinder vertical (default orientation) -->
    <body name="cylinder1" pos="-0.3 0 0.3">
      <geom type="cylinder" size="0.05 0.1" rgba="1 1 0 1"/>
    </body>

    <!-- Object 5: Cylinder horizontal along Y (90° around X) -->
    <body name="cylinder2" pos="0 -0.3 0.3">
      <geom type="cylinder" size="0.05 0.1" pos="0 0 0" euler="1.5708 0 0" rgba="1 0 1 1"/>
    </body>

    <!-- Object 6: Cylinder horizontal along X (90° around Y) -->
    <body name="cylinder3" pos="0 0 0.5">
      <geom type="cylinder" size="0.05 0.1" pos="0 0 0" euler="0 1.5708 0" rgba="0 1 1 1"/>
    </body>

  </worldbody>

</mujoco>`
  },
  newtons_cradle: {
    name: "Newton's Cradle",
    xml: `<?xml version="1.0"?>
<mujoco>
  <option timestep="0.002" gravity="0 0 -9.81"/>
  
  <default>
    <geom solref="0.02 1" solimp="0.9 0.95 0.001" friction="0.1 0.05 0.001"/>
    <joint damping="0.001" armature="0.0001"/>
  </default>

  <worldbody>
    <light pos="0 0 1.5" dir="0 0 -1" diffuse="0.7 0.7 0.7"/>
    <geom name="ground" type="plane" size="2 2 0.1" rgba="0.92 0.92 0.92 1" contype="0" conaffinity="0"/>

    <!-- Simple frame - horizontal bar -->
    <body name="frame" pos="0 0 0.5">
      <geom type="box" size="0.15 0.01 0.01" rgba="0.2 0.2 0.2 1"/>

      <!-- Simple pendulum: 3 balls for easier testing -->
      <!-- Ball radius: 0.03, rod length: 0.15, spacing: 0.08 -->
      <body name="p0" pos="-0.08 0 0">
        <joint name="hinge0" type="hinge" axis="0 1 0" damping="0.001"/>
        <geom name="rod0" type="cylinder" size="0.003 0.075" pos="0 0 -0.075" euler="1.5708 0 0" rgba="0.3 0.3 0.3 1"/>
        <geom name="ball0" type="sphere" pos="0 0 -0.15" size="0.03" rgba="0.8 0.2 0.2 1" mass="0.1"/>
      </body>

      <body name="p1" pos="0 0 0">
        <joint name="hinge1" type="hinge" axis="0 1 0" damping="0.001"/>
        <geom name="rod1" type="cylinder" size="0.003 0.075" pos="0 0 -0.075" euler="1.5708 0 0" rgba="0.3 0.3 0.3 1"/>
        <geom name="ball1" type="sphere" pos="0 0 -0.15" size="0.03" rgba="0.8 0.2 0.2 1" mass="0.1"/>
      </body>

      <body name="p2" pos="0.08 0 0">
        <joint name="hinge2" type="hinge" axis="0 1 0" damping="0.001"/>
        <geom name="rod2" type="cylinder" size="0.003 0.075" pos="0 0 -0.075" euler="1.5708 0 0" rgba="0.3 0.3 0.3 1"/>
        <geom name="ball2" type="sphere" pos="0 0 -0.15" size="0.03" rgba="0.8 0.2 0.2 1" mass="0.1"/>
      </body>
    </body>

  </worldbody>

  <!-- Initial condition: pull back the leftmost ball -->
  <keyframe>
    <key name="start" qpos="0.5 0 0"/>
  </keyframe>

</mujoco>`
  }
}

class MuJoCoViewer {
  constructor(canvasId) {
    this.canvas = document.getElementById(canvasId)
    this.isPlaying = true
    this.currentScenario = null
    this.mujoco = null
    this.model = null
    this.data = null
    
    // Three.js setup
    this.scene = null
    this.camera = null
    this.renderer = null
    this.meshes = new Map()
    this.bodyToMeshMap = new Map() // Map MuJoCo body ID to Three.js mesh // Map MuJoCo body IDs to Three.js meshes
    
    // Drag interaction state
    this.isDragging = false
    this.dragTarget = null // Body ID being dragged
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.pivotPosition = new THREE.Vector3(0, 1.0, 0) // Pivot point in Three.js coords
    this.rodLength = 0.6 // Rod length
    
    // Set canvas size
    this.resizeCanvas()
    window.addEventListener('resize', () => this.resizeCanvas())
    
    // Initialize Three.js
    this.initThreeJS()
    
    // Initialize MuJoCo
    this.init()
  }
  
  // Helper: Convert MuJoCo Z-up position to Three.js Y-up
  mujocoToThreePos(mujocoPos) {
    // MuJoCo: X=right, Y=forward, Z=up
    // Three.js: X=right, Y=up, Z=forward (out of screen)
    // Conversion: (x, z, -y)
    return [mujocoPos[0], mujocoPos[2], -mujocoPos[1]]
  }
  
  // Helper: Convert MuJoCo Z-up Euler to Three.js Y-up Euler
  mujocoToThreeEuler(mujocoEuler) {
    const [rx, ry, rz] = mujocoEuler
    const result = [rz, rx, ry]
    // Debug logging
    if (Math.abs(rx) > 0.1 || Math.abs(ry) > 0.1 || Math.abs(rz) > 0.1) {
      console.warn(`Euler conversion: MuJoCo (${(rx*180/Math.PI).toFixed(1)}, ${(ry*180/Math.PI).toFixed(1)}, ${(rz*180/Math.PI).toFixed(1)}) -> Three.js (${(result[0]*180/Math.PI).toFixed(1)}, ${(result[1]*180/Math.PI).toFixed(1)}, ${(result[2]*180/Math.PI).toFixed(1)})`)
    }
    return result
  }
  
  initThreeJS() {
    // Create scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x0a0a0a)
    
    // Create camera
    const aspect = this.canvas.width / this.canvas.height
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.01, 1000)
    // Position camera to view Newton's Cradle from the side
    // Frame is at z=0.6 in MuJoCo -> y=0.6 in Three.js, balls hang down to y~0.42
    // View from side at angle to see the swinging motion
    this.camera.position.set(0, 0.5, 1.2)
    this.camera.lookAt(0, 0.5, 0)
    
    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true
    })
    this.renderer.setSize(this.canvas.width, this.canvas.height)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    
    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3)
    this.scene.add(ambientLight)
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
    directionalLight.position.set(0, 0, 3)
    this.scene.add(directionalLight)
    
    // Add orbit controls (optional - can add later)
    // For now, we'll use a fixed camera
  }
  
  resizeCanvas() {
    const container = this.canvas.parentElement
    this.canvas.width = container.clientWidth
    this.canvas.height = container.clientHeight
    
    if (this.renderer && this.camera) {
      this.renderer.setSize(this.canvas.width, this.canvas.height)
      this.camera.aspect = this.canvas.width / this.canvas.height
      this.camera.updateProjectionMatrix()
    }
  }
  
  setupDragControls() {
    // Use the renderer's DOM element (which is the actual canvas being rendered to)
    const targetCanvas = this.renderer ? this.renderer.domElement : this.canvas
    
    if (!targetCanvas) {
      console.error('Cannot setup drag controls: no canvas element')
      return
    }
    
    console.warn('Setting up drag controls on:', targetCanvas, 'Renderer:', this.renderer, 'Meshes:', this.meshes.size)
    
    // Mouse events
    targetCanvas.addEventListener('mousedown', (e) => {
      console.warn('Mouse down event received')
      this.onPointerDown(e)
    }, { passive: false })
    targetCanvas.addEventListener('mousemove', (e) => this.onPointerMove(e), { passive: false })
    targetCanvas.addEventListener('mouseup', (e) => this.onPointerUp(e), { passive: false })
    
    // Touch events
    targetCanvas.addEventListener('touchstart', (e) => {
      e.preventDefault()
      console.warn('Touch start event')
      this.onPointerDown(e.touches[0])
    }, { passive: false })
    targetCanvas.addEventListener('touchmove', (e) => {
      e.preventDefault()
      this.onPointerMove(e.touches[0])
    }, { passive: false })
    targetCanvas.addEventListener('touchend', (e) => {
      e.preventDefault()
      this.onPointerUp(e)
    }, { passive: false })
    
    // Also set pointer-events style to ensure canvas is interactive
    targetCanvas.style.pointerEvents = 'auto'
    targetCanvas.style.cursor = 'default'
    targetCanvas.style.touchAction = 'none' // Prevent default touch behaviors
    
    console.warn('Drag controls setup complete')
  }
  
  getMousePosition(event) {
    // Use renderer's DOM element if available, otherwise fall back to canvas
    const targetCanvas = this.renderer ? this.renderer.domElement : this.canvas
    const rect = targetCanvas.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((event.clientY - rect.top) / rect.height) * 2 + 1
    }
  }
  
  onPointerDown(event) {
    if (!this.camera || !this.scene) {
      console.log('onPointerDown: camera or scene not ready')
      return
    }
    
    const pos = this.getMousePosition(event)
    this.mouse.x = pos.x
    this.mouse.y = pos.y
    
    console.log('Mouse down at:', pos, 'Mesh count:', this.meshes.size)
    
    // Raycast to find clicked object
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const meshArray = Array.from(this.meshes.values())
    console.log('Raycasting against', meshArray.length, 'meshes:', meshArray.map(m => m.name))
    const intersects = this.raycaster.intersectObjects(meshArray, false)
    
    console.log('Intersects:', intersects.length)
    if (intersects.length > 0) {
      console.log('First intersect:', intersects[0].object.name, intersects[0].object)
    }
    
    if (intersects.length > 0) {
      const clickedMesh = intersects[0].object
      const meshName = clickedMesh.name || ''
      console.log('Clicked mesh:', meshName)
      
      // Check if it's the pendulum ball (body with sphere geom)
      // The ball should be body_3_sphere based on the console logs
      if (meshName.includes('sphere') || meshName.includes('ball')) {
        // For pendulum, we know the ball is the last sphere (body_3_sphere)
        // and it's connected via a hinge joint
        console.log('Starting drag on pendulum ball')
        this.isDragging = true
        this.dragTarget = 'pendulum_ball' // Use string identifier
        const targetCanvas = this.renderer ? this.renderer.domElement : this.canvas
        if (targetCanvas) {
          targetCanvas.style.cursor = 'grabbing'
        }
      }
    } else {
      console.log('No mesh intersected, but starting drag anyway for testing')
      // For testing: allow dragging anywhere on canvas
      this.isDragging = true
      this.dragTarget = 'pendulum_ball'
      const targetCanvas = this.renderer ? this.renderer.domElement : this.canvas
      if (targetCanvas) {
        targetCanvas.style.cursor = 'grabbing'
      }
    }
  }
  
  onPointerMove(event) {
    if (!this.isDragging || !this.dragTarget || !this.model || !this.data || !this.camera) {
      if (this.isDragging) {
        console.log('Drag blocked:', { isDragging: this.isDragging, dragTarget: this.dragTarget, hasModel: !!this.model, hasData: !!this.data, hasCamera: !!this.camera })
      }
      return
    }
    
    const pos = this.getMousePosition(event)
    this.mouse.x = pos.x
    this.mouse.y = pos.y
    
    // Raycast to find intersection with a plane at the pendulum's height
    this.raycaster.setFromCamera(this.mouse, this.camera)
    
    // Create a plane at the pivot height (Y = 1.0 in Three.js, which is Z = 1.0 in MuJoCo)
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -this.pivotPosition.y)
    const intersectPoint = new THREE.Vector3()
    const hasIntersection = this.raycaster.ray.intersectPlane(plane, intersectPoint)
    
    if (!hasIntersection) {
      console.log('No plane intersection')
      return
    }
    
    // Constrain the ball to be at rod length from pivot
    const direction = new THREE.Vector3().subVectors(intersectPoint, this.pivotPosition)
    const distance = direction.length()
    if (distance > 0) {
      direction.normalize()
      direction.multiplyScalar(this.rodLength)
      const constrainedPos = new THREE.Vector3().addVectors(this.pivotPosition, direction)
      
      // Convert Three.js position back to MuJoCo coordinates
      // Three.js: (x, y, z) -> MuJoCo: (x, -z, y)
      const mujocoX = constrainedPos.x
      const mujocoY = -constrainedPos.z
      const mujocoZ = constrainedPos.y
      
      // Calculate the angle for the hinge joint
      // The joint rotates around Y axis in MuJoCo
      // Angle from vertical: atan2(x, z - pivotZ)
      const pivotZ = 1.0 // Pivot Z in MuJoCo
      const angle = Math.atan2(mujocoX, mujocoZ - pivotZ)
      
      console.log('Setting angle:', angle, 'rad =', angle * 180 / Math.PI, 'deg')
      
      // Set the joint angle directly
      if (this.data.qpos && this.model.njnt > 0) {
        // Find the hinge joint (should be the first joint)
        this.data.qpos[0] = angle
        // Reset velocities when dragging
        if (this.data.qvel) {
          this.data.qvel[0] = 0
        }
      } else {
        console.log('Cannot set angle:', { hasQpos: !!this.data.qpos, njnt: this.model?.njnt })
      }
    }
  }
  
  onPointerUp(event) {
    if (this.isDragging) {
      console.log('Mouse up - ending drag')
      this.isDragging = false
      this.dragTarget = null
      const targetCanvas = this.renderer ? this.renderer.domElement : this.canvas
      if (targetCanvas) {
        targetCanvas.style.cursor = 'default'
      }
    }
  }
  
  async init() {
    this.updateStatus('Loading MuJoCo WASM...', 'loading')
    
    try {
      // Initialize Three.js first to show something
      if (!this.scene) {
        console.error('Three.js scene not initialized')
        this.updateStatus('Error: Three.js not initialized', 'error')
        return
      }
      
      // Show a simple test object while loading
      const testGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5)
      const testMaterial = new THREE.MeshStandardMaterial({ color: 0x4a9eff })
      const testMesh = new THREE.Mesh(testGeometry, testMaterial)
      testMesh.position.set(0, 1, 0)
      this.scene.add(testMesh)
      this.renderer.render(this.scene, this.camera)
      
      this.updateStatus('Loading MuJoCo...', 'loading')
      
      await this.loadMuJoCoWASM()
      
      // Remove test mesh
      this.scene.remove(testMesh)
      
      // Load default scenario - check URL param first, then default
      const urlParams = new URLSearchParams(window.location.search)
      const scenarioParam = urlParams.get('scenario')
      const defaultScenario = (scenarioParam && SCENARIOS[scenarioParam]) ? scenarioParam : 'pendulum'
      console.log(`Loading scenario: ${defaultScenario} (URL param: ${scenarioParam})`)
      await this.loadScenario(defaultScenario)
      
      // Setup drag controls after scenario is loaded (renderer should be ready)
      this.setupDragControls()
      
      this.updateStatus('Ready', 'success')
    } catch (error) {
      console.error('Failed to initialize MuJoCo:', error)
      this.updateStatus(`Error: ${error.message}`, 'error')
      this.showPlaceholder()
    }
  }
  
  async loadMuJoCoWASM() {
    try {
      // Initialize MuJoCo WASM
      this.mujoco = await loadMujoco()
      
      // Mount virtual file system
      if (this.mujoco.FS) {
        this.mujoco.FS.mkdir('/working')
        this.mujoco.FS.mount(this.mujoco.MEMFS, { root: '.' }, '/working')
        console.log('MuJoCo WASM loaded and file system mounted')
      }
      
      return true
    } catch (error) {
      console.error('Failed to load MuJoCo WASM:', error)
      throw error
    }
  }
  
  async loadScenario(scenarioKey) {
    const scenario = SCENARIOS[scenarioKey]
    if (!scenario) {
      throw new Error(`Unknown scenario: ${scenarioKey}`)
    }
    
    this.updateStatus(`Loading ${scenario.name}...`, 'loading')
    this.currentScenario = scenarioKey
    
    if (!this.mujoco) {
      await this.loadMuJoCoWASM()
    }
    
    try {
      // Write XML to virtual file system
      const filename = `/working/${scenarioKey}.xml`
      this.mujoco.FS.writeFile(filename, scenario.xml)
      
      // Load model from XML
      try {
        this.model = this.mujoco.MjModel.loadFromXML(filename)
        if (!this.model) {
          throw new Error('Failed to load model from XML')
        }
      } catch (error) {
        console.error('MuJoCo XML compilation error:', error)
        console.error('Error details:', error.toString())
        // Try to get more error info if available
        if (this.mujoco.getLastError) {
          const lastError = this.mujoco.getLastError()
          console.error('MuJoCo last error:', lastError)
        }
        throw new Error(`MuJoCo XML error: ${error.message || error.toString()}`)
      }
      
      // Create data structure
      this.data = new this.mujoco.MjData(this.model)
      
      // Reset simulation (API uses mj_resetData, not MjResetData)
      if (this.mujoco.mj_resetData) {
        this.mujoco.mj_resetData(this.model, this.data)
      } else if (this.mujoco.MjResetData) {
        // Fallback for different API versions
        this.mujoco.MjResetData(this.model, this.data)
      } else if (this.data.reset) {
        // Alternative: use data.reset() if available
        this.data.reset()
      }
      
      // Set initial conditions for Newton's Cradle (pull back first ball)
      // Note: The XML now uses keyframes, but we can also set qpos directly
      if (scenarioKey === 'newtons_cradle' && this.data.qpos) {
        // With hinge joints, qpos contains joint angles
        // First ball (hinge0) should be pulled back
        if (this.data.qpos.length >= 5) {
          // Pull back first ball ~0.55 radians (from keyframe)
          this.data.qpos[0] = 0.55
          // Keep others at 0
          this.data.qpos[1] = 0
          this.data.qpos[2] = 0
          this.data.qpos[3] = 0
          this.data.qpos[4] = 0
        }
        // Set initial velocity to zero
        if (this.data.qvel && this.data.qvel.length >= 5) {
          for (let i = 0; i < 5; i++) {
            this.data.qvel[i] = 0
          }
        }
      }
      
      // Start rendering
      this.startRendering()
      
      this.updateStatus(`Loaded: ${scenario.name}`, 'success')
    } catch (error) {
      console.error('Failed to load scenario:', error)
      this.updateStatus(`Error loading ${scenario.name}`, 'error')
      this.showPlaceholder()
    }
  }
  
  startRendering() {
    if (!this.model || !this.data || !this.mujoco) return
    
    // Create Three.js meshes from MuJoCo model
    this.createMeshes()
    
    // Start simulation and rendering loop
    this.simulate()
  }
  
  createMeshes() {
    // Clear existing meshes
    this.meshes.forEach(mesh => {
      this.scene.remove(mesh)
    })
    this.meshes.clear()
    this.bodyToMeshMap.clear()
    
    // Parse XML to create initial meshes
    // We'll use the scenario XML to create the visual representation
    const scenario = SCENARIOS[this.currentScenario]
    if (scenario) {
      this.parseXMLToMeshes(scenario.xml)
    }
    
    // Ground plane is created from XML, no need for manual one
  }
  
  addReferenceAxes() {
    // Add XYZ axes at origin for reference
    // X = red, Y = green, Z = blue
    const axisLength = 0.3
    const arrowLength = 0.1
    const arrowRadius = 0.02
    
    // X axis (red) - right
    const xAxis = new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0), // direction
      new THREE.Vector3(0, 0, 0), // origin
      axisLength,
      0xff0000, // red
      arrowLength,
      arrowRadius
    )
    this.scene.add(xAxis)
    
    // Y axis (green) - up in Three.js
    const yAxis = new THREE.ArrowHelper(
      new THREE.Vector3(0, 1, 0), // direction
      new THREE.Vector3(0, 0, 0), // origin
      axisLength,
      0x00ff00, // green
      arrowLength,
      arrowRadius
    )
    this.scene.add(yAxis)
    
    // Z axis (blue) - forward (out of screen)
    const zAxis = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, 1), // direction
      new THREE.Vector3(0, 0, 0), // origin
      axisLength,
      0x0000ff, // blue
      arrowLength,
      arrowRadius
    )
    this.scene.add(zAxis)
    
    // Add rotation arrows (circular arrows) to show rotation direction
    // X rotation (around X axis) - red circle in YZ plane
    const xRotGeometry = new THREE.TorusGeometry(0.15, 0.01, 8, 32, Math.PI * 2)
    const xRotMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000, side: THREE.DoubleSide })
    const xRotMesh = new THREE.Mesh(xRotGeometry, xRotMaterial)
    xRotMesh.rotation.z = Math.PI / 2 // Rotate to YZ plane
    xRotMesh.position.set(0.2, 0, 0)
    this.scene.add(xRotMesh)
    
    // Y rotation (around Y axis) - green circle in XZ plane
    const yRotGeometry = new THREE.TorusGeometry(0.15, 0.01, 8, 32, Math.PI * 2)
    const yRotMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00, side: THREE.DoubleSide })
    const yRotMesh = new THREE.Mesh(yRotGeometry, yRotMaterial)
    yRotMesh.rotation.x = Math.PI / 2 // Rotate to XZ plane
    yRotMesh.position.set(0, 0.2, 0)
    this.scene.add(yRotMesh)
    
    // Z rotation (around Z axis) - blue circle in XY plane
    const zRotGeometry = new THREE.TorusGeometry(0.15, 0.01, 8, 32, Math.PI * 2)
    const zRotMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff, side: THREE.DoubleSide })
    const zRotMesh = new THREE.Mesh(zRotGeometry, zRotMaterial)
    // Already in XY plane (default)
    zRotMesh.position.set(0, 0, 0.2)
    this.scene.add(zRotMesh)
    
    // Add labels (optional - can use sprites or HTML overlay)
    console.log('Reference axes added: X=red (right), Y=green (up), Z=blue (forward)')
  }
  
  parseXMLToMeshes(xml) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(xml, 'text/xml')
    
    const worldbody = doc.querySelector('worldbody')
    if (!worldbody) {
      console.warn('No worldbody found in XML')
      return
    }
    
    // Add reference axes at origin
    this.addReferenceAxes()
    
    // Track mesh index for mapping to MuJoCo bodies
    // We'll map meshes to bodies in order (body 1 = first mesh, body 2 = second mesh, etc.)
    let meshIndex = 0
    
    // First, parse geoms directly in worldbody (like ground plane, lights)
    const worldbodyGeoms = Array.from(worldbody.children).filter(
      child => child.tagName === 'geom'
    )
    worldbodyGeoms.forEach(geom => {
      const type = geom.getAttribute('type')
      const sizeAttr = geom.getAttribute('size')
      const rgbaAttr = geom.getAttribute('rgba')
      
      const size = sizeAttr ? sizeAttr.split(' ').map(parseFloat) : [1]
      const color = rgbaAttr ? rgbaAttr.split(' ').map(parseFloat) : [0.92, 0.92, 0.92, 1]
      
      let geometry
      if (type === 'plane') {
        geometry = new THREE.PlaneGeometry((size[0] || 1) * 2, (size[1] || 1) * 2)
        geometry.rotateX(-Math.PI / 2) // Horizontal plane
      } else {
        geometry = new THREE.PlaneGeometry(4, 4)
        geometry.rotateX(-Math.PI / 2)
      }
      
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(color[0], color[1], color[2]),
        side: THREE.DoubleSide
      })
      
      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(0, 0, 0) // Ground plane at y=0
      this.scene.add(mesh)
      this.meshes.set('worldbody_geom_' + this.meshes.size, mesh)
    })
    
    // Parse bodies recursively with proper hierarchy
    const parseBody = (bodyElement, parentTransform = new THREE.Matrix4()) => {
      // Get body position relative to parent
      // MuJoCo uses Z-up coordinate system: X=right, Y=forward, Z=up
      // Three.js uses Y-up coordinate system: X=right, Y=up, Z=forward
      // Conversion: MuJoCo (x, y, z) -> Three.js (x, z, y)
      //   - X stays X (right)
      //   - Z becomes Y (up)
      //   - Y becomes Z (forward/back) - but check sign!
      const posAttr = bodyElement.getAttribute('pos')
      const mujocoPos = posAttr ? posAttr.split(' ').map(parseFloat) : [0, 0, 0]
      const pos = this.mujocoToThreePos(mujocoPos)
      
      // Get body rotation (euler angles)
      const eulerAttr = bodyElement.getAttribute('euler')
      const mujocoEuler = eulerAttr ? eulerAttr.split(' ').map(parseFloat) : [0, 0, 0]
      const euler = this.mujocoToThreeEuler(mujocoEuler)
      
      // Create transform for this body
      const bodyTransform = new THREE.Matrix4()
      const rotation = new THREE.Euler(euler[0], euler[1], euler[2], 'XYZ')
      bodyTransform.makeRotationFromEuler(rotation)
      bodyTransform.setPosition(pos[0], pos[1], pos[2])
      bodyTransform.premultiply(parentTransform)
      
      // Process geometries in this body (direct children only, not nested)
      const directGeoms = Array.from(bodyElement.children).filter(
        child => child.tagName === 'geom'
      )
      
      directGeoms.forEach((geom, idx) => {
        const type = geom.getAttribute('type')
        const sizeAttr = geom.getAttribute('size')
        const rgbaAttr = geom.getAttribute('rgba')
        const geomPosAttr = geom.getAttribute('pos')
        const geomEulerAttr = geom.getAttribute('euler')
        
        const size = sizeAttr ? sizeAttr.split(' ').map(parseFloat) : [0.1]
        const color = rgbaAttr 
          ? rgbaAttr.split(' ').map(parseFloat)
          : [0.8, 0.2, 0.2, 1]
        
        // Get geom position relative to body (if specified) - in MuJoCo coordinates
        const geomMujocoPos = geomPosAttr ? geomPosAttr.split(' ').map(parseFloat) : [0, 0, 0]
        const geomPos = this.mujocoToThreePos(geomMujocoPos)
        
        // Get geom rotation (if specified) - in MuJoCo coordinates
        const geomMujocoEuler = geomEulerAttr ? geomEulerAttr.split(' ').map(parseFloat) : [0, 0, 0]
        const geomEuler = this.mujocoToThreeEuler(geomMujocoEuler)
        
        // Create transform for this geom (body transform + geom offset)
        // First create local transform for geom (position and rotation relative to body)
        const geomLocalTransform = new THREE.Matrix4()
        if (geomEulerAttr) {
          // Create rotation from converted Euler angles
          // Use 'XYZ' order which is standard for Three.js
          const geomRotation = new THREE.Euler(geomEuler[0], geomEuler[1], geomEuler[2], 'XYZ')
          geomLocalTransform.makeRotationFromEuler(geomRotation)
          // Debug: Log the Euler angles we're using
          console.warn(`Cylinder euler: MuJoCo (${geomMujocoEuler.map(x => (x * 180 / Math.PI).toFixed(1)).join(', ')}°) -> Three.js (${geomEuler.map(x => (x * 180 / Math.PI).toFixed(1)).join(', ')}°)`)
        }
        // Set position in local space (relative to body)
        geomLocalTransform.setPosition(geomPos[0], geomPos[1], geomPos[2])
        // Apply body transform first, then geom local transform
        // This ensures geom is positioned and rotated relative to body, then body transform is applied
        const geomTransform = bodyTransform.clone().multiply(geomLocalTransform)
        
        // Extract final position and rotation
        const position = new THREE.Vector3()
        const quaternion = new THREE.Quaternion()
        const scale = new THREE.Vector3()
        geomTransform.decompose(position, quaternion, scale)
        
        let geometry
        switch (type) {
          case 'sphere':
            geometry = new THREE.SphereGeometry(size[0] || 0.1, 16, 16)
            break
          case 'box':
            geometry = new THREE.BoxGeometry(
              (size[0] || 0.1) * 2, // MuJoCo uses half-sizes
              (size[1] || size[0] || 0.1) * 2,
              (size[2] || size[0] || 0.1) * 2
            )
            break
          case 'cylinder':
            // Cylinder: size[0] = radius, size[1] = half-height
            // MuJoCo cylinders are oriented along Z-axis (vertical in Z-up)
            // Three.js cylinders are along Y-axis (vertical in Y-up) - already correct!
            geometry = new THREE.CylinderGeometry(
              size[0] || 0.1,
              size[0] || 0.1,
              (size[1] || 0.5) * 2, // Convert half-height to full height
              32
            )
            // No geometry rotation needed - euler rotation from XML will be applied to mesh
            break
          case 'plane':
            geometry = new THREE.PlaneGeometry(
              (size[0] || 1) * 2,
              (size[1] || 1) * 2
            )
            // MuJoCo plane is horizontal (XY plane in Z-up)
            // Three.js plane is vertical by default, rotate to horizontal (XZ plane in Y-up)
            geometry.rotateX(-Math.PI / 2)
            break
          default:
            geometry = new THREE.SphereGeometry(0.1, 16, 16)
        }
        
        const material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(color[0], color[1], color[2]),
          opacity: color[3] !== undefined ? color[3] : 1,
          transparent: color[3] !== undefined && color[3] < 1
        })
        
        const mesh = new THREE.Mesh(geometry, material)
        // Apply the combined transform (body + geom offset)
        mesh.position.copy(position)
        mesh.quaternion.copy(quaternion)
        
        // Store geom's local transform (offset relative to body) in userData
        // This allows us to recompute the mesh position when the body moves
        // IMPORTANT: Store in MuJoCo coordinates (body local space), not Three.js coords
        // We'll convert when applying the transform in updateMeshes()
        mesh.userData.geomLocalPosMujoco = new THREE.Vector3(geomMujocoPos[0], geomMujocoPos[1], geomMujocoPos[2])
        mesh.userData.geomLocalEulerMujoco = geomMujocoEuler ? [...geomMujocoEuler] : [0, 0, 0]
        
        // Store mesh
        const meshKey = `body_${this.meshes.size}_${type}`
        mesh.name = meshKey // Set name for raycasting
        this.scene.add(mesh)
        this.meshes.set(meshKey, mesh)
        
        // Debug: Log mesh position and rotation
        const eulerRot = new THREE.Euler().setFromQuaternion(quaternion, 'XYZ')
        // Also log quaternion to see actual rotation
        console.warn(`Mesh ${meshKey}: pos=(${mesh.position.x.toFixed(2)}, ${mesh.position.y.toFixed(2)}, ${mesh.position.z.toFixed(2)}) rot=(${(eulerRot.x * 180 / Math.PI).toFixed(1)}°, ${(eulerRot.y * 180 / Math.PI).toFixed(1)}°, ${(eulerRot.z * 180 / Math.PI).toFixed(1)}°) quat=(${quaternion.x.toFixed(3)}, ${quaternion.y.toFixed(3)}, ${quaternion.z.toFixed(3)}, ${quaternion.w.toFixed(3)})`)
        
        // Map this mesh to a MuJoCo body ID (skip world body = 0)
        // We'll use meshIndex + 1 to map to body IDs
        const bodyId = meshIndex + 1
        if (!this.bodyToMeshMap.has(bodyId)) {
          this.bodyToMeshMap.set(bodyId, [])
        }
        this.bodyToMeshMap.get(bodyId).push(mesh)
        meshIndex++
      })
      
      // Process child bodies (direct children only)
      const directBodies = Array.from(bodyElement.children).filter(
        child => child.tagName === 'body'
      )
      
      directBodies.forEach((childBody, idx) => {
        parseBody(childBody, bodyTransform)
      })
    }
    
    // Start parsing bodies from worldbody (worldbody itself is not a body)
    const worldbodyBodies = Array.from(worldbody.children).filter(
      child => child.tagName === 'body'
    )
    worldbodyBodies.forEach(body => {
      parseBody(body, new THREE.Matrix4())
    })
    
    console.log(`Created ${this.meshes.size} meshes from XML`)
  }
  
  updateMeshes() {
    if (!this.model || !this.data || !this.mujoco) return
    
    // Update mesh positions from MuJoCo simulation data
    // data.xpos is a Float32Array with 3 floats per body: [x0, y0, z0, x1, y1, z1, ...]
    // data.xquat is a Float32Array with 4 floats per body: [w0, x0, y0, z0, w1, x1, y1, z1, ...]
    
    // Apply the same coordinate conversion as XML parsing: MuJoCo (x, y, z) -> Three.js (x, z, y)
    if (this.data.xpos && this.data.xquat) {
      const nbody = this.model.nbody || 0
      
      for (let bodyId = 1; bodyId < nbody; bodyId++) { // Skip world body (id=0)
        const posIdx = bodyId * 3
        const quatIdx = bodyId * 4
        
        // Get position in MuJoCo coordinates
        const mujocoX = this.data.xpos[posIdx]
        const mujocoY = this.data.xpos[posIdx + 1]
        const mujocoZ = this.data.xpos[posIdx + 2]
        
        // Convert to Three.js coordinates: (x, z, -y)
        const threeX = mujocoX
        const threeY = mujocoZ
        const threeZ = -mujocoY
        
        // Get quaternion (MuJoCo uses w, x, y, z)
        const quatW = this.data.xquat[quatIdx]
        const quatX = this.data.xquat[quatIdx + 1]
        const quatY = this.data.xquat[quatIdx + 2]
        const quatZ = this.data.xquat[quatIdx + 3]
        
        // Convert quaternion from MuJoCo Z-up to Three.js Y-up
        // This is a simplified conversion - quaternion conversion is complex
        // For now, use the quaternion directly and see if it works
        // TODO: Implement proper quaternion conversion for coordinate system change
        const bodyQuat = new THREE.Quaternion(quatX, quatZ, quatY, quatW)
        const bodyPos = new THREE.Vector3(threeX, threeY, threeZ)
        
        // Update all meshes associated with this body
        const meshes = this.bodyToMeshMap.get(bodyId)
        if (meshes) {
          meshes.forEach(mesh => {
            // If mesh has geom local transform stored, apply it relative to body
            if (mesh.userData.geomLocalPosMujoco) {
              // Convert geom local position from MuJoCo to Three.js coordinates
              const geomLocalPosThree = this.mujocoToThreePos([
                mesh.userData.geomLocalPosMujoco.x,
                mesh.userData.geomLocalPosMujoco.y,
                mesh.userData.geomLocalPosMujoco.z
              ])
              const geomLocalPosVec = new THREE.Vector3(geomLocalPosThree[0], geomLocalPosThree[1], geomLocalPosThree[2])
              
              // Convert geom local rotation from MuJoCo to Three.js
              const geomLocalEulerThree = this.mujocoToThreeEuler(mesh.userData.geomLocalEulerMujoco)
              const geomLocalQuat = new THREE.Quaternion()
              const geomLocalEuler = new THREE.Euler(geomLocalEulerThree[0], geomLocalEulerThree[1], geomLocalEulerThree[2], 'XYZ')
              geomLocalQuat.setFromEuler(geomLocalEuler)
              
              // Create body transform matrix
              const bodyTransform = new THREE.Matrix4()
              bodyTransform.compose(bodyPos, bodyQuat, new THREE.Vector3(1, 1, 1))
              
              // Create geom local transform matrix (in body's local space, but Three.js coords)
              const geomLocalTransform = new THREE.Matrix4()
              geomLocalTransform.compose(geomLocalPosVec, geomLocalQuat, new THREE.Vector3(1, 1, 1))
              
              // Combine: bodyTransform * geomLocalTransform
              // This applies the geom offset in the body's rotated coordinate system
              const finalTransform = bodyTransform.clone().multiply(geomLocalTransform)
              
              // Extract final position and rotation
              const finalPos = new THREE.Vector3()
              const finalQuat = new THREE.Quaternion()
              const finalScale = new THREE.Vector3()
              finalTransform.decompose(finalPos, finalQuat, finalScale)
              
              mesh.position.copy(finalPos)
              mesh.quaternion.copy(finalQuat)
            } else {
              // No geom offset, just use body position/rotation directly
              mesh.position.copy(bodyPos)
              mesh.quaternion.copy(bodyQuat)
            }
          })
        }
      }
    }
  }
  
  simulate() {
    if (!this.isPlaying || !this.model || !this.data) return
    
    // Step simulation (API uses mj_step, not MjStep)
    if (this.mujoco.mj_step) {
      this.mujoco.mj_step(this.model, this.data)
    } else if (this.mujoco.MjStep) {
      // Fallback for different API versions
      this.mujoco.MjStep(this.model, this.data)
    }
    
    // Update Three.js meshes
    this.updateMeshes()
    
    // Render scene
    this.renderer.render(this.scene, this.camera)
    
    // Continue animation loop
    requestAnimationFrame(() => this.simulate())
  }
  
  showPlaceholder() {
    // Clear scene and show placeholder
    if (this.scene) {
      // Remove all meshes except lights
      const objectsToRemove = []
      this.scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          objectsToRemove.push(object)
        }
      })
      objectsToRemove.forEach(obj => this.scene.remove(obj))
      
      // Add placeholder text (using a simple geometry)
      const geometry = new THREE.BoxGeometry(0.5, 0.1, 0.1)
      const material = new THREE.MeshStandardMaterial({ color: 0x4a9eff })
      const placeholder = new THREE.Mesh(geometry, material)
      placeholder.position.set(0, 1, 0)
      this.scene.add(placeholder)
      
      this.renderer.render(this.scene, this.camera)
    }
  }
  
  play() {
    this.isPlaying = true
    if (this.model && this.data) {
      this.simulate()
    }
  }
  
  pause() {
    this.isPlaying = false
  }
  
  reset() {
    if (this.currentScenario) {
      this.loadScenario(this.currentScenario)
    } else if (this.customXML) {
      this.loadCustomXML(this.customXML)
    }
  }
  
  async loadCustomXML(xml) {
    this.customXML = xml
    this.updateStatus('Loading custom simulation...', 'loading')
    
    if (!this.mujoco) {
      await this.loadMuJoCoWASM()
    }
    
    try {
      // Clean up previous model if exists
      if (this.data) {
        this.data.delete()
        this.data = null
      }
      if (this.model) {
        this.model.delete()
        this.model = null
      }
      
      // Write XML to virtual file system
      const filename = '/working/custom.xml'
      this.mujoco.FS.writeFile(filename, xml)
      
      // Load model from XML
      this.model = this.mujoco.MjModel.loadFromXML(filename)
      if (!this.model) {
        throw new Error('Failed to load model from XML')
      }
      
      // Create data structure
      this.data = new this.mujoco.MjData(this.model)
      
      // Reset simulation (API uses mj_resetData, not MjResetData)
      if (this.mujoco.mj_resetData) {
        this.mujoco.mj_resetData(this.model, this.data)
      } else if (this.mujoco.MjResetData) {
        // Fallback for different API versions
        this.mujoco.MjResetData(this.model, this.data)
      } else if (this.data.reset) {
        // Alternative: use data.reset() if available
        this.data.reset()
      }
      
      // Start rendering
      this.startRendering()
      
      this.updateStatus('Custom simulation loaded', 'success')
    } catch (error) {
      console.error('Failed to load custom XML:', error)
      this.updateStatus(`Error: ${error.message}`, 'error')
      this.showPlaceholder()
    }
  }
  
  updateStatus(message, type = '') {
    // Update status in chat panel
    const statusEl = document.getElementById('newton-status')
    if (statusEl) {
      statusEl.textContent = message
      statusEl.className = `newton-status ${type}`
    }
  }
}


// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initViewer)
} else {
  initViewer()
}

function initViewer() {
  // Initialize viewer
  const viewer = new MuJoCoViewer('mujoco-canvas')
  
  // Handle scenario buttons (from chat panel)
  window.addEventListener('loadScenario', async (e) => {
    const scenario = e.detail.scenario
    await viewer.loadScenario(scenario)
    
    // Update status in chat panel
    const statusEl = document.getElementById('newton-status')
    if (statusEl) {
      statusEl.textContent = `Loaded: ${SCENARIOS[scenario]?.name || scenario}`
      statusEl.className = 'newton-status success'
    }
  })
  
  // Handle play/pause (from chat panel)
  window.addEventListener('togglePlayPause', () => {
    if (viewer.isPlaying) {
      viewer.pause()
      const btn = document.getElementById('newton-play-pause')
      if (btn) btn.textContent = '▶️ Play'
    } else {
      viewer.play()
      const btn = document.getElementById('newton-play-pause')
      if (btn) btn.textContent = '⏸️ Pause'
    }
  })
  
  // Handle reset (from chat panel)
  window.addEventListener('resetSimulation', () => {
    viewer.reset()
    const statusEl = document.getElementById('newton-status')
    if (statusEl) {
      statusEl.textContent = 'Reset'
      statusEl.className = 'newton-status'
    }
  })
  
  // Update status in chat panel when viewer status changes
  const originalUpdateStatus = viewer.updateStatus
  viewer.updateStatus = function(message, type = '') {
    // Call original method
    if (originalUpdateStatus) {
      originalUpdateStatus.call(this, message, type)
    }
    
    // Also update chat panel status
    const statusEl = document.getElementById('newton-status')
    if (statusEl) {
      statusEl.textContent = message
      statusEl.className = `newton-status ${type}`
    }
  }
  
  // Handle URL parameters for direct simulation loading
  // Note: This is a fallback - the init() function should already handle URL params
  // But we keep this for cases where the viewer is initialized before URL params are checked
  const urlParams = new URLSearchParams(window.location.search)
  const scenarioParam = urlParams.get('scenario')
  if (scenarioParam && SCENARIOS[scenarioParam] && !viewer.currentScenario) {
    console.log(`Loading scenario from URL param: ${scenarioParam}`)
    viewer.loadScenario(scenarioParam)
  }
  
  const xmlParam = urlParams.get('xml')
  if (xmlParam) {
    // Load custom XML
    const decodedXml = decodeURIComponent(xmlParam)
    viewer.loadCustomXML(decodedXml)
  }
}
